import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import invoiceLogo from "../../../assets/images/invoicing-icon.svg";
import { useFormData } from "../../../helpers/FormDataProvider";
import CommercialAirlineNavigationBar from "../../../components/element/CommercialAirlineNavigationBar";
import DateLocalizationProvider from "../../../components/element/DateLocalizationProvider";
import Loader from "../../../components/element/Loader";
import Pagination from "../../../components/element/Pagination";
import SnackbarAlert from "../../../components/element/SnackbarAlert";
import useAirlineAdminApi from "../../../services/api/useAirlineAdminApi";
import { StorageContext } from "../../../services/StorageProvider";
import {
  API_DATE_FORMAT,
  BankType,
  DATE_FORMAT,
  DefaultConfigValues,
  INVOICE_STATUS,
  PAGINATION_PAGE_SIZE,
  PAYMENT_CONSTANTS,
  PAYMENT_DIALOG,
  PAYMENT_FORM_FIELDS,
  PAYMENT_FORM_VALUES,
} from "../../../utils/constants";
import ROUTE_PATHS from "../../../utils/routePaths";
import { getUrls } from "../../../utils/urls";
import NewInvoiceTable from "./Tables/NewInvoiceTable";
import OverdueTable from "./Tables/OverdueTable";
import PaidTable from "./Tables/PaidTable";
import PendingInvoiceTable from "./Tables/PendingInvoiceTable";
import Filters from "./Views/Filters";
import InvoiceTabs from "./Views/InvoiceTabs";
import { getServiceUrls } from "../../../services/serviceUrls";

function InvoiceScreen() {
  const { userData } = useFormData();
  const SERVICE_URL = getServiceUrls();
  const { getInvoices, submitWireTransferPayment, getBankConfig, downloadCommercialInvoiceDetails } =
    useAirlineAdminApi();
  const { airportData, associatedAirlineData } = useContext(StorageContext);
  const [tab, setTab] = useState(() => {
    const savedTab = localStorage.getItem("selectedInvoiceTab");
    return savedTab ? parseInt(savedTab, 10) : 0;
  });
  const [filters, setFilters] = useState({
    invoiceNumber: "",
    invoiceDate: "",
    invoiceDueDate: "",
  });
  const [selectedAirline, setSelectedAirline] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => {
    return sessionStorage.getItem("invoiceCurrentPage")
      ? Number(sessionStorage.getItem("invoiceCurrentPage"))
      : 0;
  });

  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState(
    PAYMENT_CONSTANTS.DEFAULT_PAYMENT_TYPE
  );
  const [openWireReferenceDialog, setOpenWireReferenceDialog] = useState(false);
  const [openBankDetailsDialog, setOpenBankDetailsDialog] = useState(false);
  const [wireReference, setWireReference] = useState("");
  const [wireAmount, setWireAmount] = useState(0);
  const [wireAmountInput, setWireAmountInput] = useState("");
  const [wireDate, setWireDate] = useState(null);
  const [selectedBank, setSelectedBank] = useState("BSD");
  const queryClient = useQueryClient();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
  });

  const { data: bankConfig, isLoading: bankConfigLoading } = useQuery({
    queryKey: ["bankConfig"],
    queryFn: () => getBankConfig(SERVICE_URL.REACT_APP_PROFILE_ENV),
  });

  const getStatusForTab = (tabIndex) => {
    switch (tabIndex) {
      case 0:
        return INVOICE_STATUS.NEW_INVOICE;
      case 1:
        return INVOICE_STATUS.PAID;
      // case 2:
      //   return INVOICE_STATUS.PENDING;
      case 2:
        return INVOICE_STATUS.OVERDUE;
      default:
        return INVOICE_STATUS.NEW_INVOICE;
    }
  };

  useEffect(() => {
    if (associatedAirlineData?.data?.length > 0) {
      setSelectedAirline(associatedAirlineData?.data[0]);
    }
    sessionStorage.setItem("invoiceCurrentPage", currentPage);
  }, [associatedAirlineData, currentPage]);

  const { data: invoiceData, isLoading: invoiceLoading } = useQuery({
    queryKey: ["invoices", selectedAirline?.id, tab, currentPage, filters],
    queryFn: () =>
      getInvoices(
        selectedAirline?.id,
        getStatusForTab(tab),
        currentPage,
        PAGINATION_PAGE_SIZE,
        filters.invoiceNumber,
        filters.invoiceDate,
        filters.invoiceDueDate
      ),
    enabled: !!selectedAirline,
  });

  const rows = invoiceData?.data?.content || [];
  const grandTotal = rows.reduce((acc, row) => acc + row.invoiceAmount, 0);



  const handleTabChange = (event, newValue) => {
    setCurrentPage(0);
    setTab(newValue);
    localStorage.setItem("selectedInvoiceTab", newValue.toString());
    // Clear filters when switching tabs
    setFilters({
      invoiceNumber: "",
      invoiceDate: "",
      invoiceDueDate: "",
    });
  };

  const handleAirlineChange = (event) => {
    setCurrentPage(0);
    const selectedId = event.target.value;
    const selectedAirline = associatedAirlineData?.data?.find(
      (airline) => airline.id === selectedId
    );
    setSelectedAirline(selectedAirline);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const handleClearFilters = () => {
    setCurrentPage(0);
    setFilters({
      invoiceNumber: "",
      invoiceDate: "",
      invoiceDueDate: "",
    });
  };

  const handleApplyFilters = (filters, selectedFromDate, selectedDueDate) => {
    setCurrentPage(0);
    setFilters({
      invoiceNumber: filters.invoiceNumber,
      invoiceDate: selectedFromDate
        ? selectedFromDate.format("YYYY-MM-DD")
        : "",
      invoiceDueDate: selectedDueDate
        ? selectedDueDate.format("YYYY-MM-DD")
        : "",
    });
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage + 1 < invoiceData?.data?.totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePayClick = () => {
    if (selectedInvoices.length === 0) {
      setSnackbar({ open: false, message: "" });
      setTimeout(() => {
        setSnackbar({
          open: true,
          message: "No Invoice is selected",
          type: "error",
        });
      }, 100);
      return;
    }


    setOpenPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
  };

  const wireTransferMutation = useMutation({
    mutationFn: ({ paymentData }) => submitWireTransferPayment(paymentData),
    onSuccess: (response) => {
      setOpenWireReferenceDialog(false);
      setWireReference("");
      setWireAmount(0);
      setWireAmountInput("");
      setWireDate(null);
      setSelectedBank("BSD");
      // Reset selected invoices
      setSelectedInvoices([]);
      setTimeout(() => {
        setSnackbar({
          open: true,
          message: response?.data,
          type: "success",
        });
      }, 100);

      queryClient.invalidateQueries({
        queryKey: ["invoices", selectedAirline?.id, tab, currentPage, filters],
      });
    },
    onError: (error) => {
      setTimeout(() => {
        setSnackbar({
          open: true,
          message: error?.errorMessage,
          type: "error",
        });
      }, 100);
    },
  });

  const handleWireReferenceSubmit = async () => {
    const paymentData = {
      paymentReferenceNumber: wireReference,
      paymentAmount: wireAmount,
      paymentDate: dayjs(wireDate).format(API_DATE_FORMAT),
      bankAccountNumber:
        selectedBank === BankType.BSD
          ? bankConfig?.data?.bsdAccountNumber
          : bankConfig?.data?.usdAccountNumber,
      invoiceIds: selectedInvoices,
    };

    wireTransferMutation.mutate({ paymentData: paymentData });
  };

  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);

  const downloadCommercialInvoiceDetailsMutation = useMutation({
    mutationFn: ({ dynamicsInvoiceId }) => {
      setDownloadingInvoiceId(dynamicsInvoiceId);
      return downloadCommercialInvoiceDetails(dynamicsInvoiceId);
    },
    onSuccess: () => {
      setDownloadingInvoiceId(null);
      setSnackbar({
        open: true,
        message: "Invoice downloaded successfully",
        type: "success",
      });
    },
    onError: (error) => {
      setDownloadingInvoiceId(null);
      setSnackbar({
        open: true,
        message: error?.errorMessage || error?.message || "Failed to download invoice",
        type: "error",
      });
    },
  });

  const handleDownloadInvoice = (row) => {
    downloadCommercialInvoiceDetailsMutation.mutate({ dynamicsInvoiceId: row.dynamicsInvoiceId });
  };

  const handlePaymentSubmit = () => {
    if (selectedPaymentType === PAYMENT_CONSTANTS.PAYMENT_TYPES.WIRE) {
      setOpenPaymentDialog(false);
      setOpenBankDetailsDialog(true);
    } else {
      setOpenPaymentDialog(false);
      const selectedRows = rows.filter((row) =>
        selectedInvoices.includes(row.id)
      );
      const dynamicsCustomerNumber = selectedRows[0]?.dynamicsCustomerNumber;

      const form = document.createElement("form");
      form.method = "POST";
      form.action = DefaultConfigValues.REACT_APP_PLUGNPAY_URL;
      form.target = "_blank";
      form.style.display = "none";
      const fields = {
        [PAYMENT_FORM_FIELDS.GATEWAY_ACCOUNT]:
          DefaultConfigValues.REACT_APP_PLUGNPAY_GATEWAY_ACCOUNT,
        [PAYMENT_FORM_FIELDS.AMOUNT]: selectedTotal.toFixed(2),
        [PAYMENT_FORM_FIELDS.CURRENCY]: PAYMENT_CONSTANTS.CURRENCY,
        [PAYMENT_FORM_FIELDS.CUSTOM_NAME_1]: PAYMENT_FORM_VALUES.CUSTOM_NAME_1,
        [PAYMENT_FORM_FIELDS.CUSTOM_VALUE_1]: selectedRows
          .map((row) => row.dynamicsInvoiceNumber)
          .join(","),
        [PAYMENT_FORM_FIELDS.CUSTOM_NAME_2]: PAYMENT_FORM_VALUES.CUSTOM_NAME_2,
        [PAYMENT_FORM_FIELDS.CUSTOM_VALUE_2]: dynamicsCustomerNumber,
        [PAYMENT_FORM_FIELDS.PAYMENT_TYPE]: PAYMENT_FORM_VALUES.PAYMENT_TYPE,
        [PAYMENT_FORM_FIELDS.BILLING_COUNTRY]: PAYMENT_CONSTANTS.COUNTRY,
        [PAYMENT_FORM_FIELDS.RESPONSE_URL]: getUrls().PAYMENT_RESPONSE_URL,
        [PAYMENT_FORM_FIELDS.EMAIL]: userData?.email,
        [PAYMENT_FORM_FIELDS.TRANSITION_TYPE]: PAYMENT_CONSTANTS.TRANSITION_TYPE,
        [PAYMENT_FORM_FIELDS.SUBMIT]: PAYMENT_FORM_VALUES.SUBMIT,
      };
      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });
      const submitButton = document.createElement("input");
      submitButton.type = "submit";
      submitButton.style.display = "none";
      form.appendChild(submitButton);
      const handleFocus = () => {
        window.location.reload();
        window.removeEventListener("focus", handleFocus);
      };
      window.addEventListener("focus", handleFocus);
      document.body.appendChild(form);
      submitButton.click();
      form.remove();
    }
  };

  const handleBankDetailsContinue = () => {
    setOpenBankDetailsDialog(false);
    setOpenWireReferenceDialog(true);
  };

  const handleDateChange = (newValue) => {
    setWireDate(newValue);
  };
  const selectedTotal = rows
    .filter((row) => selectedInvoices.includes(row.id))
    .reduce((acc, row) => (acc + row.invoiceAmount) - row.totalPaymentReceived, 0);

  return (
    <div>
      <Box p={0} position="relative" minHeight="100vh">
        <CommercialAirlineNavigationBar
          title="Invoices"
          navigationLogo={invoiceLogo}
          fullWidthContent={true}
          backContent={
            <div className="navigation-header">
              <Link
                to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
                className="navigation-header-link"
              >
                Home/
              </Link>
              Invoices
            </div>
          }
          airlineData={associatedAirlineData}
          onChangeAirline={handleAirlineChange}
          selectedAirline={selectedAirline}
        />
        <Box p={2}>
          <InvoiceTabs value={tab} onChange={handleTabChange} />
          {invoiceLoading && <Loader />}
          {!invoiceLoading && (
            <>
              {tab === 0 && (
                <Box p={3} pb={8}>
                  <Filters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    onApplyFilters={handleApplyFilters}
                    showBankDetails={true}
                    bankConfig={bankConfig}
                  />

                  <NewInvoiceTable
                    invoices={rows}
                    selectedTotal={selectedTotal}
                    isLoading={invoiceLoading}
                    selectedAirlineId={selectedAirline?.id}
                    onPayClick={handlePayClick}
                    selectedInvoices={selectedInvoices}
                    setSelectedInvoices={setSelectedInvoices}
                    currentPage={currentPage}
                  />
                </Box>
              )}
              {tab === 1 && (
                <Box p={3}>
                  <Filters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    onApplyFilters={handleApplyFilters}
                    showBankDetails={false}
                  />

                  <PaidTable
                    invoices={rows}
                    airportData={airportData}
                    isLoading={invoiceLoading}
                    selectedAirlineId={selectedAirline?.id}
                    currentPage={currentPage}
                    onDownloadInvoice={handleDownloadInvoice}
                    downloadingInvoiceId={downloadingInvoiceId}
                  />
                </Box>
              )}
              {/* {tab === 2 && (
                <Box p={3}>
                  <Filters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    onApplyFilters={handleApplyFilters}
                    showBankDetails={false}
                  />

                  <PendingInvoiceTable
                    invoices={rows}
                    airportData={airportData}
                    isLoading={invoiceLoading}
                    selectedAirlineId={selectedAirline?.id}
                    currentPage={currentPage}
                  />
                </Box>
              )} */}
              {tab === 2 && (
                <Box p={3}>
                  <Filters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    onApplyFilters={handleApplyFilters}
                    showBankDetails={true}
                    bankConfig={bankConfig}
                  />

                  <OverdueTable
                    invoices={rows}
                    selectedTotal={selectedTotal}
                    isLoading={invoiceLoading}
                    selectedAirlineId={selectedAirline?.id}
                    onPayClick={handlePayClick}
                    selectedInvoices={selectedInvoices}
                    setSelectedInvoices={setSelectedInvoices}
                    currentPage={currentPage}
                  />
                </Box>
              )}
            </>
          )}
        </Box>

        {invoiceData?.data?.content &&
          invoiceData?.data?.content.length > 0 && (
            <Pagination
              currentPage={currentPage + 1}
              totalPages={invoiceData?.data?.totalPages}
              onPrevPage={handlePrevPage}
              onNextPage={handleNextPage}
            />
          )}

        <Dialog open={openPaymentDialog ?? false} onClose={handleClosePaymentDialog}>
          <DialogContent>
            <div className="payment-options-container">
              <h2>{PAYMENT_DIALOG.TITLE}</h2>
              <RadioGroup
                defaultValue={PAYMENT_CONSTANTS.DEFAULT_PAYMENT_TYPE}
                value={selectedPaymentType}
                onChange={(e) => setSelectedPaymentType(e.target.value)}
              >
                <FormControlLabel
                  value={PAYMENT_CONSTANTS.PAYMENT_TYPES.WIRE}
                  control={<Radio />}
                  label={PAYMENT_CONSTANTS.PAYMENT_LABELS.WIRE}
                />
                <FormControlLabel
                  value={PAYMENT_CONSTANTS.PAYMENT_TYPES.CREDIT}
                  control={<Radio />}
                  label={PAYMENT_CONSTANTS.PAYMENT_LABELS.CREDIT}
                />
              </RadioGroup>
              <Button
                variant="contained"
                fullWidth
                onClick={handlePaymentSubmit}
                sx={{ mt: 2 }}
              >
                {PAYMENT_DIALOG.CONTINUE_BUTTON}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={openWireReferenceDialog ?? false}
          onClose={() => setOpenWireReferenceDialog(false)}
          data-wire-reference="true"
        >
          <DialogContent>
            <div className="wire-reference-container">
              <h2>{PAYMENT_DIALOG.WIRE_REFERENCE.TITLE}</h2>
              <TextField
                fullWidth
                label={PAYMENT_DIALOG.WIRE_REFERENCE.PAYMENT_REFERENCE_LABEL}
                value={wireReference}
                onChange={(e) => setWireReference(e.target.value)}
                margin="none"
                placeholder={PAYMENT_DIALOG.WIRE_REFERENCE.PAYMENT_REFERENCE_PLACEHOLDER}
              />
              <TextField
                fullWidth
                label={PAYMENT_DIALOG.WIRE_REFERENCE.PAYMENT_AMOUNT_LABEL}
                type="number"
                value={wireAmountInput}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || value === "0") {
                    setWireAmountInput(value);
                    setWireAmount(0);
                  } else {
                    // Check if the value has more than 2 decimal places
                    const decimalPlaces = value.split('.')[1]?.length || 0;
                    if (decimalPlaces <= 2) {
                      setWireAmountInput(value);
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        setWireAmount(numValue);
                      }
                    }
                  }
                }}
                margin="none"
                inputProps={{
                  style: { textAlign: 'left' },
                  step: "0.01",
                  min: "0"
                }}
                placeholder={PAYMENT_DIALOG.WIRE_REFERENCE.PAYMENT_AMOUNT_PLACEHOLDER}
              />
              <DateLocalizationProvider>
                <FormControl variant="outlined" fullWidth margin="none">
                  <MobileDatePicker
                    label={PAYMENT_DIALOG.WIRE_REFERENCE.PAYMENT_DATE_LABEL}
                    format={DATE_FORMAT}
                    value={wireDate}
                    onChange={handleDateChange}
                    maxDate={dayjs()}
                    className="wire-reference-datepicker"
                  />
                </FormControl>
              </DateLocalizationProvider>
              <p className="wire-reference-helper-text">
                {PAYMENT_DIALOG.WIRE_REFERENCE.HELPER_TEXT}{" "}
                <span
                  className="wire-reference-link"
                  onClick={() => {
                    setOpenWireReferenceDialog(false);
                    setOpenPaymentDialog(true);
                  }}
                >
                  {PAYMENT_DIALOG.WIRE_REFERENCE.LINK_TEXT}
                </span>{" "}
                {PAYMENT_DIALOG.WIRE_REFERENCE.LINK_SUFFIX}
              </p>
              <Button
                variant="contained"
                fullWidth
                onClick={handleWireReferenceSubmit}
                sx={{ mt: 2 }}
                disabled={!wireReference.trim() || wireAmount <= 0 || !wireDate}
                endIcon={
                  wireTransferMutation.isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {PAYMENT_DIALOG.WIRE_REFERENCE.SAVE_BUTTON}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={openBankDetailsDialog ?? false}
          onClose={() => setOpenBankDetailsDialog(false)}
        >
          <DialogContent>
            <div className="bank-details-container">
              <h2>Choose Bank</h2>
              {bankConfigLoading ? (
                <CircularProgress />
              ) : (
                <RadioGroup
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                >
                  <FormControlLabel
                    value="BSD"
                    control={<Radio />}
                    label={bankConfig?.data?.bsdBankName || "BSD Bank"}
                  />
                  <FormControlLabel
                    value="USD"
                    control={<Radio />}
                    label={bankConfig?.data?.usdBeneficiaryBank || "USD Bank"}
                  />
                </RadioGroup>
              )}
              <Button
                variant="contained"
                fullWidth
                onClick={handleBankDetailsContinue}
                sx={{ mt: 2 }}
                disabled={bankConfigLoading}
              >
                Continue
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </Box>

      {snackbar.open && (
        <SnackbarAlert type={snackbar.type}>{snackbar.message}</SnackbarAlert>
      )}
    </div>
  );
}

export default InvoiceScreen;
